import React, { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import {
  Form,
  FormProvider,
  ValidatedSelect,
  ValidatedTextArea,
} from '@tq/ui-components/dist/components/forms-engine';
import { AttentionMessage, Box } from '@tq/ui-components';

import { useReasonsForLateResponse } from 'api';
import { CharacterCount } from 'components';
import {
  reasonForLateResponseDetailState,
  reasonForLateResponseIdState,
  reasonForLateReasonIsOtherState,
} from 'store';
import { isOverdue } from 'tools';
import { contentWidth, selectComponentWidth } from '../../constants';
import { ConsultationResponseProps } from '../../types';

export const ResponseOverdueForm: React.FC<ConsultationResponseProps> = ({ consultation }) => {
  const { t } = useTranslation('consultation_response');
  const isConsultationOverdue = isOverdue(consultation);

  const [reasonForLateResponseId, setReasonForLateResponseId] = useRecoilState(
    reasonForLateResponseIdState
  );
  const [reasonForLateResponse, setReasonForLateResponseDetail] = useRecoilState(
    reasonForLateResponseDetailState
  );
  const [responseLateReasonIsOther, setResponseLateReasonIsOther] = useRecoilState(
    reasonForLateReasonIsOtherState
  );

  const { data: lateReasonsData } = useReasonsForLateResponse();

  useEffect(() => {
    setResponseLateReasonIsOther(
      reasonForLateResponseId ===
        lateReasonsData?.reasonsForLateResponse.find((r) => r.label === 'Other')?.value
    );
  }, [lateReasonsData, reasonForLateResponseId, setResponseLateReasonIsOther]);

  return (
    <>
      {isConsultationOverdue && (
        <FormProvider mode='onChange'>
          <Form id='overdue_response_form'>
            <Box>
              <AttentionMessage
                mb={6}
                status='warning'
                title={t(
                  'late_response_reason_title',
                  'Please select a reason for the consultation response being overdue'
                )}
                variant='inline'
                w='100%'
                maxWidth={{ base: '100%', lg: contentWidth }}
              />
              <ValidatedSelect
                id='late-response-drop-down'
                aria-label={t('choose_late_response_reason_label', 'Late Response reason')}
                defaultValue={reasonForLateResponseId?.toString()}
                hideLabelColon={true}
                label={t('choose_late_response_reason', 'Reason for late response')}
                mr={1}
                onChange={(value: string) => setReasonForLateResponseId(+value)}
                placeholder={`- ${t('select', 'Select')} -`}
                py={2}
                validation={{
                  required: {
                    value: true,
                    message: t(
                      'late_response_required_error',
                      'Please select a late response reason'
                    ),
                  },
                }}
                w={{ base: '100%', lg: selectComponentWidth }}
              >
                {lateReasonsData?.reasonsForLateResponse.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </ValidatedSelect>
              {responseLateReasonIsOther && (
                <>
                  <ValidatedTextArea
                    form='overdue_response_form'
                    id='lateReasonText'
                    hideLabelColon={true}
                    label={t(
                      'late_response_detail_label',
                      'If other, please provide a reason for late response'
                    )}
                    mode='onChange'
                    placeholder={t('late_response_detail_placeholder', 'Enter reason here')}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      setReasonForLateResponseDetail(event.target.value)
                    }
                    data-testid='overdue-reason-textarea'
                    validation={{
                      required: {
                        value: true,
                        message: t(
                          'late_response_detail_required_error',
                          'Please enter reason for late response'
                        ),
                      },
                      maxLength: {
                        value: 400,
                        message: t(
                          'late_response_detail_length_error',
                          'You have exceeded the character limit'
                        ),
                      },
                    }}
                    w='100%'
                    maxWidth={{ base: '100%', lg: contentWidth }}
                  />
                  <CharacterCount value={reasonForLateResponse} maxCount={400} />
                </>
              )}
            </Box>
          </Form>
        </FormProvider>
      )}
    </>
  );
};
